import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { isNil, omitBy } from 'lodash';
import { Link, routerStore } from 'core';
import { frameStore } from 'apps/components/Frame/appManager';
import { Dropdown } from 'libs/rendering/gltf/components/headerDropdown';
import { insertDivider } from 'utils/jsxUtils';
import { DiffIcon } from 'icons/common/cadroom';
import { useCADManager } from 'hooks';
import { SubHeaderHeight } from 'styles/constants';
import { Tooltip } from 'components';
import { UploadRevisionStyled } from './revisions.styled';

interface RevisionsProps {
  dataTestIdPrefix?: string;
}

const Revisions = observer(({ dataTestIdPrefix = '' }: RevisionsProps) => {
  const { sharedFileManager, file } = frameStore;
  const { isDiffing, shouldShowDiffOrMeasurement } = useCADManager();

  const currentFileName = file?.filename;

  const revisionsConnection = sharedFileManager?.revisionsConn;
  const { totalCount = 0, hasNextPage, endCursor } = revisionsConnection || {};
  const showDropdown = totalCount >= 1;

  useEffect(() => {
    if (hasNextPage) {
      revisionsConnection?.loadMore();
    }
  }, [totalCount, hasNextPage, endCursor]);

  const { route } = routerStore;
  const { v, diff, panel, ...otherParams } = route.params;

  const revisions = revisionsConnection.arrayApp.apps;
  const diffingStatus = isDiffing && !!diff;

  const currentRevisionIndex = revisions.findIndex(
    (r: { version: string }) => r.version === v,
  );

  // first revision not v params
  const currentRevision =
    revisions[currentRevisionIndex > 0 ? currentRevisionIndex : 0];
  const diffVersion = revisions[currentRevisionIndex + 1 || 1];

  const linkParams = diff
    ? {
        ...otherParams,
        v: currentRevisionIndex === 0 ? null : v,
        panel,
        diff: null,
        diffV: null,
      }
    : {
        ...otherParams,
        v,
        panel,
        diff: diffVersion?.file?.id,
        diffV: diffVersion?.version,
      };

  const openTabConfig = {
    title: currentRevision?.file?.filename || currentFileName,
    iconType: diff && !shouldShowDiffOrMeasurement ? 'file' : 'comparison',
  };

  const hasLastDiffVersion = revisions.length >= 2 && diffVersion;

  const shouldShowDiffIcon =
    (diffingStatus || hasLastDiffVersion) &&
    currentRevision &&
    shouldShowDiffOrMeasurement;

  return (
    <div className='flex items-center'>
      <Tooltip delayShow={200} place='bottom' id='diffTrigger' />
      {insertDivider([
        showDropdown && !diffingStatus && (
          <div>
            <Dropdown
              showDiff={revisions.length >= 2 && shouldShowDiffOrMeasurement}
              iconColor='#000'
              titleStyle={{
                color: '#000',
              }}
              contentStyle={{
                maxWidth: 'unset',
              }}
              revisionsConnection={revisionsConnection}
              selectedRevision={sharedFileManager.sharedFile?.revision}
            />
          </div>
        ),
        shouldShowDiffIcon && (
          <div
            style={{ height: SubHeaderHeight }}
            className={`px-2 flex items-center cursor-pointer hover:opacity-70 ${
              diffingStatus ? ' bg-wfGrey200' : ''
            }`}
            data-position='bottom'
            data-for='diffTrigger'
            data-tip={
              diffingStatus
                ? 'Close comparison'
                : 'Compare with previous version'
            }
          >
            <Link
              name={route.name}
              params={omitBy(linkParams, isNil)}
              openTab={openTabConfig}
            >
              <DiffIcon />
            </Link>
          </div>
        ),
        sharedFileManager?.sharedFile?.canUpdate &&
          !sharedFileManager?.waitingForConversion &&
          !diffingStatus && (
            <div className='pl-2 h-full'>
              <UploadRevisionStyled
                aria-label='Upload revision'
                data-testid={`${dataTestIdPrefix}upload-revision`}
                onClick={() => {
                  sharedFileManager.OpenRevisionModal();
                }}
              >
                Upload New Version
              </UploadRevisionStyled>
            </div>
          ),
      ])}
    </div>
  );
});

export default Revisions;
